import { SearchType } from 'connect/web/src/stores/search-store';
import { action, computed, observable } from 'mobx';
import type {
  OnlineClinicService,
  OnlineLocationDto,
  OnlineServiceCategory
} from 'tm3-core/api.dtos';

class SearchBarStore {
  @observable searchMode = SearchType.Initial;
  @observable serviceId = 'All';
  @observable locationId = 'All';

  @observable allServices: OnlineClinicService[] = [];
  @observable allCategories: OnlineServiceCategory[] = [];
  @observable allLocations: OnlineLocationDto[] = [];

  constructor(
    locations: OnlineLocationDto[],
    services: OnlineClinicService[],
    categories: OnlineServiceCategory[]
  ) {
    this.allServices = services;
    this.allCategories = categories;
    this.allLocations = locations;
  }

  @computed
  get selectedService(): OnlineClinicService | null {
    if (!this.serviceId) {
      return null;
    }
    const serviceId = this.serviceId.split('/')[0];
    return this.allServices.find((service) => service.id === serviceId);
  }

  @computed
  get route() {
    if (this.serviceId === 'All') {
      return '';
    }

    const ids = this.serviceId.split('/');
    const serviceId = ids[0];
    const parentId = ids[1];
    const service = this.allServices.find((ser) => ser.id === serviceId);
    const category = this.allCategories.find((cat) => cat.id === parentId);

    const route = '/' + category!.urlSlug + '/' + service!.urlSlug;

    return this.generateRoute(route, category!.parentId);
  }

  generateRoute(route: string, parentId: string): string {
    if (parentId == null) {
      return route;
    }

    const category = this.allCategories.find((cat) => cat.id === parentId);

    route = '/' + category!.urlSlug + route;

    return this.generateRoute(route, category!.parentId);
  }

  @computed
  get filteredServices() {
    let services = this.allServices.filter((service) => {
      if (service.isHidden) {
        return;
      }

      if (this.searchMode === SearchType.Class) {
        return service.isClassService;
      }

      if (this.searchMode === SearchType.Appointment) {
        return !service.isClassService;
      }

      return true;
    });

    if (!!this.locationId && this.locationId !== 'All') {
      services = services.filter((x) => !x.excludeLocations.includes(this.locationId));
    }

    return services;
  }

  @computed
  get filteredTypes() {
    let services = this.allServices;
    if (!!this.locationId && this.locationId !== 'All') {
      services = services.filter((x) => !x.excludeLocations.includes(this.locationId));
    }

    let types = [SearchType.Initial];

    if (services.some((service) => service.isClassService)) {
      types.push(SearchType.Class);
    }

    if (services.some((service) => !service.isClassService)) {
      types.push(SearchType.Appointment);
    }
    return types;
  }

  @computed
  get filteredLocations() {
    let locations = this.allLocations;
    if (this.serviceId !== 'All') {
      locations = locations.filter((loc) => {
        return !this.selectedService!.excludeLocations.includes(loc.onlineGuid);
      });
    }

    if (this.searchMode !== SearchType.Initial) {
      locations = locations.filter((loc) => {
        return this.filteredServices!.some(
          (x) =>
            (this.searchMode === SearchType.Class
              ? x.isClassService
              : this.searchMode === SearchType.Appointment
                ? !x.isClassService
                : true) && !x.excludeLocations.includes(loc.onlineGuid)
        );
      });
    }

    return locations;
  }

  @computed
  get filteredCategories() {
    return this.filterCategories(this.allCategories);
  }

  filterCategories(categories: OnlineServiceCategory[]): OnlineServiceCategory[] {
    const currentLength = categories.length;

    const filteredCategories = categories.filter((cat) => {
      const serviceIds = cat.orderedServices.filter((serviceId) =>
        this.filteredServices.some((fs) => {
          return fs.id === serviceId;
        })
      );

      const subCategories = categories.filter((c) => c.parentId === cat.id);

      if (serviceIds.length === 0 && subCategories.length === 0) {
        return false;
      }

      return true;
    });

    if (filteredCategories.length === currentLength) {
      return filteredCategories;
    } else {
      return this.filterCategories(filteredCategories);
    }
  }
}

export default SearchBarStore;
